import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FiX, FiDownload, FiInfo } from 'react-icons/fi';
import './ExportDataModal.css';

// 결과 데이터 CSV 내보내기 — DBeaver 의 "Export resultset" 에 대응.
//
// 화면 그리드는 커서 페이징이라 일부만 로드돼 있다. 여기서는 서버가 쿼리를 다시 실행해
// 전체 결과를 스트리밍한다. 다운로드는 fetch+Blob 이 아니라 GET 내비게이션으로 받는다
// (Blob 은 응답 전체를 메모리에 쌓아 대용량에서 탭이 죽는다).
// SQL 은 URL 로 넘기기엔 길어서, POST 로 조건을 등록해 짧은 토큰을 받아 그 토큰을 연다.

const API_ROOT = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
const API_BASE = `${API_ROOT}/sql-developer`;

const getDelimiters = (t) => [
    { value: 'comma', label: t('dataTransfer:exp.comma') },
    { value: 'tab', label: t('dataTransfer:exp.tab') },
    { value: 'semicolon', label: t('dataTransfer:exp.semicolon') },
    { value: 'pipe', label: t('dataTransfer:exp.pipe') },
];

function defaultFileName(tabName) {
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '').replace(/-/g, '');
    const base = String(tabName || 'result').replace(/[\\/:*?"<>|]+/g, '_').trim() || 'result';
    return `${base}_${stamp}`;
}

const ExportDataModal = ({ isOpen, onClose, resultTab, connectionName }) => {
    const { t } = useTranslation();
    const [fileName, setFileName] = useState('');
    const [delimiter, setDelimiter] = useState('comma');
    const [includeHeader, setIncludeHeader] = useState(true);
    const [bom, setBom] = useState(true);
    const [quoteAll, setQuoteAll] = useState(false);
    const [nullText, setNullText] = useState('');
    const [lineEnding, setLineEnding] = useState('crlf');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const sql = useMemo(
        () => (resultTab?.sourceSql || resultTab?.query || '').trim(),
        [resultTab]
    );
    const effectiveName = fileName.trim() || defaultFileName(resultTab?.name);

    const handleExport = async () => {
        setError(null);
        if (!resultTab?.connectionId) { setError(t('dataTransfer:exp.err.noConnection')); return; }
        if (!sql) { setError(t('dataTransfer:exp.err.noSql')); return; }

        setBusy(true);
        try {
            const res = await axios.post(`${API_BASE}/export/csv/prepare`, {
                connectionId: resultTab.connectionId,
                query: sql,
                bindVariables: resultTab.bindVariables || {},
                fileName: `${effectiveName}.csv`,
                options: { delimiter, includeHeader, bom, quoteAll, nullText, lineEnding },
            });
            const token = res.data?.token;
            if (!token) throw new Error(t('dataTransfer:exp.err.noToken'));

            // GET 내비게이션 — 브라우저가 디스크로 흘려 받는다(메모리에 쌓지 않음).
            window.location.href = `${API_BASE}/export/csv/${token}`;
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setBusy(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="export-modal-overlay" onClick={onClose}>
            <div className="export-modal" onClick={e => e.stopPropagation()}>
                <div className="export-modal-header">
                    <h2><FiDownload size={16} /> {t('dataTransfer:exp.title')}</h2>
                    <button className="export-modal-close" onClick={onClose}><FiX size={18} /></button>
                </div>

                <div className="export-modal-body">
                    <div className="export-source">
                        <div className="export-source-row">
                            <span className="export-label">{t('dataTransfer:exp.connection')}</span>
                            <span>{connectionName || resultTab?.connectionId || '-'}</span>
                        </div>
                        <div className="export-source-row">
                            <span className="export-label">SQL</span>
                            <code className="export-sql" title={sql}>{sql || t('dataTransfer:exp.none')}</code>
                        </div>
                    </div>

                    <label className="export-field">
                        <span className="export-label">{t('dataTransfer:exp.fileName')}</span>
                        <div className="export-filename">
                            <input
                                type="text"
                                value={fileName}
                                placeholder={defaultFileName(resultTab?.name)}
                                onChange={e => setFileName(e.target.value)}
                            />
                            <span className="export-ext">.csv</span>
                        </div>
                    </label>

                    <label className="export-field">
                        <span className="export-label">{t('dataTransfer:exp.delimiter')}</span>
                        <select value={delimiter} onChange={e => setDelimiter(e.target.value)}>
                            {getDelimiters(t).map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                    </label>

                    <label className="export-field">
                        <span className="export-label">{t('dataTransfer:exp.newline')}</span>
                        <select value={lineEnding} onChange={e => setLineEnding(e.target.value)}>
                            <option value="crlf">CRLF (Windows · Excel)</option>
                            <option value="lf">LF (Unix)</option>
                        </select>
                    </label>

                    <label className="export-field">
                        <span className="export-label">{t('dataTransfer:exp.nullText')}</span>
                        <input
                            type="text"
                            value={nullText}
                            placeholder={t('dataTransfer:exp.blank')}
                            onChange={e => setNullText(e.target.value)}
                        />
                    </label>

                    <div className="export-checks">
                        <label>
                            <input type="checkbox" checked={includeHeader} onChange={e => setIncludeHeader(e.target.checked)} />
                            {t('dataTransfer:exp.includeHeader')}
                        </label>
                        <label title={t('dataTransfer:exp.bomNote')}>
                            <input type="checkbox" checked={bom} onChange={e => setBom(e.target.checked)} />
                            {t('dataTransfer:exp.bomLabel')} <span className="export-hint">{t('dataTransfer:exp.bomShort')}</span>
                        </label>
                        <label>
                            <input type="checkbox" checked={quoteAll} onChange={e => setQuoteAll(e.target.checked)} />
                            {t('dataTransfer:exp.quoteAll')}
                        </label>
                    </div>

                    <div className="export-note">
                        <FiInfo size={12} />
                        {/* 한 문장으로 둔다 — 세 조각 + <strong> 으로 나눠 쓰던 때는 조각 사이 공백이
                            들쭉날쭉했고 언어마다 어순이 달라 문장이 어그러졌다(신고 2026-09-18) */}
                        <span>{t('dataTransfer:exp.fullNote')}</span>
                    </div>

                    {error && <div className="export-error">{error}</div>}
                </div>

                <div className="export-modal-footer">
                    <button className="export-btn" onClick={onClose}>{t('dataTransfer:exp.cancel')}</button>
                    <button className="export-btn export-btn-primary" onClick={handleExport} disabled={busy || !sql}>
                        <FiDownload size={13} /> {busy ? t('dataTransfer:exp.preparing') : t('dataTransfer:exp.run')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportDataModal;
